import LoadingComponent from 'Components/HelperComponents/LoadingComponent'
import { EntitySelector } from 'NewVersion/components/Proposal/EntitySelector'
import { RoleSelector } from 'NewVersion/components/Proposal/RoleSelector'
import 'NewVersion/styles/ProposalsStyles.scss'
import * as path from 'Routes/Paths'
import { useEdition } from 'hooks/useEdition'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { ENS_TYPES_OBJ } from 'utils/entityUtils'

// Steps for the "Presentar" flow
const PRESENTAR_STEPS = {
  ROLE_SELECTION: 'role',
  ENTITY_SELECTION: 'entity',
  ARTIST_SELECTION: 'artist', // Only for representant flow
}

/**
 * ProposalsPresentarPage - Flow for "Presenta la teva proposta artística"
 *
 * Flow:
 * 1. Role Selection (Artist / Representant)
 * 2. Entity Selection (select or create artist/representant)
 * 3. [If Representant] Artist Selection (select artist for the proposal)
 * 4. -> Goes directly to ProposalForm
 */
const ProposalsPresentarPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { currentEdition, loading: loadingCurrentEdition } = useEdition()

  const [step, setStep] = useState(PRESENTAR_STEPS.ROLE_SELECTION)
  const [selectedRole, setSelectedRole] = useState(null) // 'artista' or 'representant'
  const [selectedEntity, setSelectedEntity] = useState(null)

  if (loadingCurrentEdition || !currentEdition) {
    return <LoadingComponent />
  }

  // If call is finished, redirect to gestio
  if (currentEdition.callIsFinished()) {
    navigate(path.PROPOSALS_GESTIO_PATH)
    return null
  }

  const handleRoleSelected = (role) => {
    setSelectedRole(role)
    setStep(PRESENTAR_STEPS.ENTITY_SELECTION)
  }

  const handleEntitySelected = (entity) => {
    // Handle "create new" request
    if (entity.__createNew) {
      if (selectedRole === ENS_TYPES_OBJ.artista.text) {
        // Go to create artist form, then proposal
        navigate(`${path.PROPOSALS_FORM_PATH}?type=${ENS_TYPES_OBJ.artista.text}&from=presentar`)
      } else {
        // Go to create representant form
        navigate(
          `${path.PROPOSALS_FORM_PATH}?type=${ENS_TYPES_OBJ.representant.text}&from=presentar`
        )
      }
      return
    }

    setSelectedEntity(entity)
    if (selectedRole === ENS_TYPES_OBJ.representant.text) {
      // Representant needs to select an artist
      setStep(PRESENTAR_STEPS.ARTIST_SELECTION)
    } else {
      // Artist goes directly to form
      navigateToForm(entity, null)
    }
  }

  const handleArtistSelected = (artist) => {
    // Handle "create new" request
    if (artist.__createNew) {
      // Navigate to create artist for this representant
      const repPath = selectedEntity['@id'] || selectedEntity.path
      navigate(
        `${path.PROPOSALS_FORM_PATH}?type=${ENS_TYPES_OBJ.representant.text}&repId=${repPath}&createArtist=true&from=presentar`
      )
      return
    }

    navigateToForm(artist, selectedEntity)
  }

  const navigateToForm = (artist, representant) => {
    // Navigate to ProposalFormPage with pre-selected entities
    const artistPath = artist['@id'] || artist.path
    if (selectedRole === ENS_TYPES_OBJ.artista.text) {
      if (artist.representant) {
        navigate(
          `${path.PROPOSALS_FORM_PATH}?type=${ENS_TYPES_OBJ.representant.text}&repId=${artist.representant}&artistId=${artistPath}&from=presentar`
        )
      } else {
        navigate(
          `${path.PROPOSALS_FORM_PATH}?type=${ENS_TYPES_OBJ.artista.text}&artistId=${artistPath}&from=presentar`
        )
      }
    } else {
      const repPath = representant['@id'] || representant.path
      navigate(
        `${path.PROPOSALS_FORM_PATH}?type=${ENS_TYPES_OBJ.representant.text}&repId=${repPath}&artistId=${artistPath}&from=presentar`
      )
    }
  }

  const handleBack = () => {
    if (step === PRESENTAR_STEPS.ARTIST_SELECTION) {
      setStep(PRESENTAR_STEPS.ENTITY_SELECTION)
    } else if (step === PRESENTAR_STEPS.ENTITY_SELECTION) {
      setStep(PRESENTAR_STEPS.ROLE_SELECTION)
      setSelectedEntity(null)
      setSelectedRole(null)
    }
  }

  return (
    <section className="proposals page-wrapper">
      <h1>{t('presentar_proposta_home_short')}</h1>

      {step === PRESENTAR_STEPS.ROLE_SELECTION && (
        <RoleSelector onRoleSelected={handleRoleSelected} />
      )}

      {step === PRESENTAR_STEPS.ENTITY_SELECTION && (
        <EntitySelector
          role={selectedRole}
          onEntitySelected={handleEntitySelected}
          onBack={handleBack}
        />
      )}

      {step === PRESENTAR_STEPS.ARTIST_SELECTION && (
        <EntitySelector
          role={ENS_TYPES_OBJ.artista.text}
          representant={selectedEntity}
          onEntitySelected={handleArtistSelected}
          onBack={handleBack}
          isArtistForRepresentant={true}
        />
      )}
    </section>
  )
}

export default ProposalsPresentarPage
